import axios, { AxiosInstance, AxiosError } from "axios";
import type {
  ThirdParty,
  Proposal,
  Order,
  Invoice,
  Project,
  Product,
  Contact,
  ProposalLine,
  Intervention,
  Contract,
  ContractLine,
  SupplierOrder,
  SupplierOrderLine,
  SupplierPrice,
  Document,
} from "@/types/dolibarr";
import { getProposalLineDisplayRef } from "@/utils/proposal-helpers";

const DOLIBARR_URL = process.env.NEXT_PUBLIC_DOLIBARR_URL || "";

// Sanitize user input for Dolibarr sqlfilters to prevent SQL injection
export function sanitizeSqlFilter(input: string): string {
  // Strip characters that could break Dolibarr's sqlfilter parser
  // Single quotes are value delimiters in sqlfilters — remove them from user input
  // Semicolons, backslashes are stripped for injection prevention
  // % and _ are SQL wildcards — escape them so user input is treated literally
  return input.replace(/['\\;%_]/g, (char) => {
    switch (char) {
      case "'": return "";       // strip — Dolibarr parser doesn't support \'
      case "\\": return "";
      case ";": return "";
      case "%": return "\\%";
      case "_": return "\\_";
      default: return char;
    }
  });
}

/** Liste Dolibarr : tableau direct ou enveloppe rare `{ data: [...] }` */
function normalizeDolibarrListResponse<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === "object" && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: T[] }).data;
  }
  return [];
}

// Pagination types
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface PaginationParams {
  sortfield?: string;
  sortorder?: "ASC" | "DESC";
  limit?: number;
  page?: number;
}

// Client-side singleton
let clientInstance: DolibarrClient | null = null;

// Login function (static, no instance needed)
// Uses the Next.js API proxy to avoid CORS/network issues from browser
export async function loginToDolibarr(
  login: string,
  password: string
): Promise<{ success: boolean; token?: string; error?: string }> {
  try {
    // Use dedicated auth proxy when in browser (no API key required)
    const loginUrl = typeof window !== 'undefined'
      ? '/api/auth/login'
      : `${DOLIBARR_URL}/api/index.php/login`;
    const response = await axios.post(
      loginUrl,
      { login, password },
      { headers: { "Content-Type": "application/json" } }
    );

    if (response.data?.success?.token) {
      return { success: true, token: response.data.success.token };
    }
    return { success: false, error: "Réponse invalide du serveur" };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      if (status === 403 || status === 401) {
        return { success: false, error: "Identifiants incorrects" };
      }
      if (error.code === "ERR_NETWORK") {
        return { success: false, error: "Impossible de contacter le serveur Dolibarr" };
      }
    }
    return { success: false, error: "Erreur de connexion" };
  }
}

/** Extrait le message d’erreur renvoyé par l’API REST Dolibarr (formats variables). */
function parseDolibarrErrorBody(data: unknown): { message: string; raw: unknown } {
  if (data == null) return { message: "", raw: data };
  if (typeof data === "string") return { message: data.trim() || "Erreur Dolibarr", raw: data };
  if (typeof data !== "object") return { message: String(data), raw: data };
  const o = data as Record<string, unknown>;
  const err = o.error;
  if (typeof err === "string" && err.trim()) return { message: err.trim(), raw: data };
  if (err && typeof err === "object" && "message" in err) {
    const m = (err as { message?: unknown }).message;
    if (m != null && String(m).trim()) return { message: String(m).trim(), raw: data };
  }
  if (typeof o.message === "string" && o.message.trim()) {
    return { message: o.message.trim(), raw: data };
  }
  try {
    return { message: JSON.stringify(data).slice(0, 800), raw: data };
  } catch {
    return { message: "Erreur Dolibarr (corps non lisible)", raw: data };
  }
}

// Error types for better error handling
export class DolibarrAPIError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string,
    public details?: unknown
  ) {
    super(message);
    this.name = "DolibarrAPIError";
  }
}

export class DolibarrClient {
  private api: AxiosInstance;
  private apiKey: string;

  get config() {
    return { apiKey: this.apiKey };
  }

  constructor(apiKey: string) {
    this.apiKey = apiKey;
    // Use Next.js API proxy to avoid CORS issues
    const baseURL = typeof window !== 'undefined' ? '/api/dolibarr' : `${DOLIBARR_URL}/api/index.php`;
    this.api = axios.create({
      baseURL,
      headers: {
        "Content-Type": "application/json",
        DOLAPIKEY: apiKey,
      },
      timeout: 30000, // 30s timeout
    });

    // Request retry interceptor
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        // Retry on network errors or 5xx (max 2 retries)
        if (
          !originalRequest._retry &&
          (error.code === "ECONNABORTED" ||
            error.code === "ERR_NETWORK" ||
            (error.response?.status && error.response.status >= 500))
        ) {
          originalRequest._retry = (originalRequest._retry || 0) + 1;
          if (originalRequest._retry <= 2) {
            await new Promise((resolve) =>
              setTimeout(resolve, 1000 * originalRequest._retry)
            );
            return this.api(originalRequest);
          }
        }

        // Enhanced error handling
        if (error.response?.status === 401) {
          // Only force logout when the auth verification endpoint itself fails
          // For other endpoints, 401 may just mean missing permissions for that module
          const requestUrl = originalRequest?.url || "";
          const isAuthCheck = requestUrl.includes("/users/info");
          if (isAuthCheck && typeof window !== "undefined") {
            localStorage.removeItem("dolapikey");
            window.location.href = "/login";
          }
          throw new DolibarrAPIError(
            isAuthCheck
              ? "Session expirée. Veuillez vous reconnecter."
              : "Accès refusé pour cette ressource.",
            401,
            "UNAUTHORIZED"
          );
        }

        if (error.response?.status === 403) {
          throw new DolibarrAPIError(
            "Accès refusé. Permissions insuffisantes.",
            403,
            "FORBIDDEN"
          );
        }

        if (error.response?.status === 400 || error.response?.status === 422) {
          const data = error.response?.data as any;
          // Extract error message from various Dolibarr response formats
          let detail: string;
          const errObj = data?.error;
          if (typeof errObj === 'string') {
            detail = errObj;
          } else if (errObj && typeof errObj === 'object') {
            detail = errObj.message || JSON.stringify(errObj);
          } else if (typeof data?.message === 'string') {
            detail = data.message;
          } else if (data?.message) {
            detail = JSON.stringify(data.message);
          } else {
            detail = "Données invalides";
          }
          throw new DolibarrAPIError(
            `Erreur de validation : ${detail}`,
            error.response.status,
            "VALIDATION_ERROR",
            data
          );
        }

        if (error.response?.status === 404) {
          throw new DolibarrAPIError(
            "Ressource introuvable.",
            404,
            "NOT_FOUND"
          );
        }

        if (error.response?.status === 500) {
          const data500 = error.response?.data as any;
          let serverDetail = "";
          if (typeof data500?.error === "string") {
            serverDetail = data500.error;
          } else if (data500?.error?.message) {
            serverDetail = data500.error.message;
          } else if (typeof data500?.message === "string") {
            serverDetail = data500.message;
          }
          throw new DolibarrAPIError(
            serverDetail
              ? `Erreur serveur Dolibarr : ${serverDetail}`
              : "Erreur serveur Dolibarr. Réessayez plus tard.",
            500,
            "SERVER_ERROR",
            data500
          );
        }

        if (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK") {
          throw new DolibarrAPIError(
            "Impossible de contacter le serveur Dolibarr. Vérifiez votre connexion.",
            0,
            "NETWORK_ERROR"
          );
        }

        throw new DolibarrAPIError(
          error.message || "Erreur inconnue",
          error.response?.status,
          "UNKNOWN_ERROR",
          error.response?.data
        );
      }
    );
  }

  // Helper method to wrap responses with pagination metadata
  private createPaginatedResponse<T>(
    data: T[],
    params: PaginationParams
  ): PaginatedResponse<T> {
    const limit = params.limit || 100;
    const page = params.page || 0;
    const hasMore = data.length === limit;

    return {
      data,
      total: data.length, // Dolibarr doesn't provide total count easily
      page,
      limit,
      hasMore,
    };
  }

  // Auth
  async verifyApiKey(): Promise<{ valid: boolean; user?: { id: string; name: string; email: string; admin: boolean } }> {
    try {
      const response = await this.api.get("/users/info");
      return {
        valid: true,
        user: {
          id: String(response.data.id || response.data.rowid || ""),
          name: `${response.data.firstname || ""} ${response.data.lastname || ""}`.trim(),
          email: response.data.email || "",
          admin: String(response.data.admin) === "1",
        },
      };
    } catch {
      return { valid: false };
    }
  }

  // ThirdParties
  private mapThirdParty(tp: ThirdParty): ThirdParty {
    // Map idprof2 (SIRET in France) to siret field for convenience
    if (!tp.siret && tp.idprof2) {
      tp.siret = tp.idprof2;
    }
    return tp;
  }

  async getThirdParties(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    mode?: number; // 1=clients, 2=prospects, 4=suppliers
    sqlfilters?: string;
  }): Promise<ThirdParty[]> {
    try {
      const response = await this.api.get("/thirdparties", { params });
      return (response.data as ThirdParty[]).map(tp => this.mapThirdParty(tp));
    } catch (e: any) {
      if (e?.status === 404 || e?.response?.status === 404) return [];
      throw e;
    }
  }

  async getThirdPartiesPaginated(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    mode?: number;
    sqlfilters?: string;
  }): Promise<PaginatedResponse<ThirdParty>> {
    const data = await this.getThirdParties(params);
    return this.createPaginatedResponse(data, params || {});
  }

  async getThirdParty(id: string): Promise<ThirdParty> {
    const response = await this.api.get(`/thirdparties/${id}`);
    return this.mapThirdParty(response.data);
  }

  async getDolibarrUser(id: string): Promise<{ id: string; login: string; firstname: string; lastname: string }> {
    const response = await this.api.get(`/users/${id}`);
    const u = response.data;
    return {
      id: String(u.id || u.rowid || id),
      login: u.login || "",
      firstname: u.firstname || "",
      lastname: u.lastname || "",
    };
  }

  async getUsers(params?: { limit?: number }): Promise<{ id: string; login: string; firstname: string; lastname: string; statut: string }[]> {
    try {
      const response = await this.api.get("/users", { params: { limit: params?.limit ?? 50 } });
      return (response.data as Record<string, unknown>[]).map((u) => ({
        id: String(u.id || u.rowid || ""),
        login: String(u.login || ""),
        firstname: String(u.firstname || ""),
        lastname: String(u.lastname || ""),
        statut: String(u.statut ?? u.status ?? "0"),
      }));
    } catch {
      return [];
    }
  }

  async createThirdParty(data: Partial<ThirdParty>): Promise<string> {
    const response = await this.api.post("/thirdparties", data);
    return response.data;
  }

  async updateThirdParty(id: string, data: Partial<ThirdParty>): Promise<ThirdParty> {
    const response = await this.api.put(`/thirdparties/${id}`, data);
    return response.data;
  }

  async deleteThirdParty(id: string): Promise<void> {
    await this.api.delete(`/thirdparties/${id}`);
  }

  async searchThirdParties(query: string): Promise<ThirdParty[]> {
    const q = sanitizeSqlFilter(query);
    return this.getThirdParties({
      limit: 20,
      sqlfilters: `((t.nom:like:'%${q}%') or (t.name_alias:like:'%${q}%'))`,
    });
  }

  // Proposals
  async getProposals(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    thirdparty_ids?: string;
    status?: string;
    sqlfilters?: string;
  }): Promise<Proposal[]> {
    try {
      const response = await this.api.get("/proposals", { params });
      return normalizeDolibarrListResponse<Proposal>(response.data);
    } catch (e: any) {
      if (e?.status === 404) return [];
      throw e;
    }
  }

  async getProposalsPaginated(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    thirdparty_ids?: string;
    status?: string;
  }): Promise<PaginatedResponse<Proposal>> {
    const data = await this.getProposals(params);
    return this.createPaginatedResponse(data, params || {});
  }

  async getProposal(id: string): Promise<Proposal> {
    const response = await this.api.get(`/proposals/${id}`);
    const proposal = response.data;

    // Fallback ref for drafts or corrupted proposals with empty ref
    if (!proposal.ref || proposal.ref === "") {
      proposal.ref = `(PROV${proposal.id})`;
    }

    // Fetch thirdparty if socid exists but thirdparty not included
    if (proposal.socid && !proposal.thirdparty) {
      try {
        const tp = await this.getThirdParty(proposal.socid);
        proposal.thirdparty = tp;
      } catch {
        // Silently fail - UI will show fallback
      }
    }

    // Unifier la colonne « Réf » (champs API + extrafields)
    if (Array.isArray(proposal.lines) && proposal.lines.length > 0) {
      proposal.lines = proposal.lines.map((line: ProposalLine) => {
        const displayRef = getProposalLineDisplayRef(line);
        if (!displayRef) return line;
        return { ...line, ref: displayRef };
      });
    }

    // Certaines instances Dolibarr / proxies renvoient un devis sans tableau `lines` : compléter via GET /lines
    if (!Array.isArray(proposal.lines) || proposal.lines.length === 0) {
      try {
        const linesRes = await this.api.get(`/proposals/${id}/lines`);
        const raw = linesRes.data;
        if (Array.isArray(raw) && raw.length > 0) {
          proposal.lines = raw as ProposalLine[];
          proposal.lines = proposal.lines.map((line: ProposalLine) => {
            const displayRef = getProposalLineDisplayRef(line);
            if (!displayRef) return line;
            return { ...line, ref: displayRef };
          });
        }
      } catch {
        /* ignore — devis réellement sans lignes */
      }
    }

    if (Array.isArray(proposal.lines) && proposal.lines.length > 0) {
      // L'API ne renvoie souvent ni ref ni product_ref malgré un produit lié : compléter via /products/{id}
      const missingRefProductIds = new Set<string>();
      for (const line of proposal.lines) {
        const refEmpty = !line.ref || String(line.ref).trim() === "";
        const fk =
          line.fk_product != null ? String(line.fk_product).trim() : "";
        if (refEmpty && fk && fk !== "0") {
          missingRefProductIds.add(fk);
        }
      }

      if (missingRefProductIds.size > 0) {
        const refByProductId = new Map<string, string>();
        const ids = [...missingRefProductIds];
        const batchSize = 15;
        for (let i = 0; i < ids.length; i += batchSize) {
          const batch = ids.slice(i, i + batchSize);
          await Promise.all(
            batch.map(async (pid) => {
              try {
                const product = await this.getProduct(pid);
                const r = product?.ref?.trim();
                if (r) refByProductId.set(pid, r);
              } catch {
                /* ignore */
              }
            })
          );
        }
        if (refByProductId.size > 0) {
          proposal.lines = proposal.lines.map((line: ProposalLine) => {
            if (line.ref && String(line.ref).trim() !== "") return line;
            const fk =
              line.fk_product != null ? String(line.fk_product).trim() : "";
            const r = fk ? refByProductId.get(fk) : undefined;
            if (!r) return line;
            return { ...line, ref: r, product_ref: r };
          });
        }
      }
    }

    return proposal;
  }

  async createProposal(data: {
    socid: string;
    date?: number;
    ref_client?: string;
    cond_reglement_code?: string;
    mode_reglement_code?: string;
    note_public?: string;
    note_private?: string;
    fin_validite?: number;
    remise_percent?: number;
    date_livraison?: number;
    fk_project?: string;
    array_options?: Record<string, unknown>;
  }): Promise<string> {
    const response = await this.api.post("/proposals", data);
    return response.data;
  }

  async updateProposal(id: string, data: Partial<Proposal>): Promise<Proposal> {
    const response = await this.api.put(`/proposals/${id}`, data);
    return response.data;
  }

  async addProposalLine(
    proposalId: string,
    line: {
      desc: string;
      subprice: number;
      qty: number;
      tva_tx?: number;
      /** Réf. écran devis : uniquement chiffres → fk_product, sinon → label Dolibarr */
      ref?: string;
      fk_product?: string;
      /** Libellé / ref courte Dolibarr (prioritaire si ref absent) */
      label?: string;
      remise_percent?: number;
      product_type?: 0 | 1 | 9; // 0=product, 1=service, 9=subtitle/section
      rang?: number;
      special_code?: number;
      pa_ht?: number;
      array_options?: Record<string, string>;
    }
  ): Promise<string> {
    /**
     * Dolibarr postLine() lit toutes les clés sur l'objet JSON ; des propriétés
     * manquantes peuvent provoquer des erreurs côté PHP selon la version.
     * On envoie donc un corps aligné sur api_proposals::postLine.
     */
    const refTrim = line.ref != null ? String(line.ref).trim() : "";
    let fkProduct: number | string = 0;
    let label = "";
    if (refTrim) {
      if (/^\d+$/.test(refTrim)) fkProduct = refTrim;
      else label = refTrim;
    } else if (line.fk_product && String(line.fk_product) !== "0") {
      fkProduct = String(line.fk_product);
    } else {
      const labelTrim = line.label != null ? String(line.label).trim() : "";
      if (labelTrim) label = labelTrim;
    }

    const paNum =
      line.pa_ht != null && !Number.isNaN(Number(line.pa_ht))
        ? Number(line.pa_ht)
        : 0;

    const sanitized: Record<string, unknown> = {
      desc: String(line.desc || ""),
      subprice: Number(line.subprice) || 0,
      qty: Number(line.qty) || 0,
      tva_tx: Number(line.tva_tx ?? 20),
      localtax1_tx: 0,
      localtax2_tx: 0,
      fk_product: fkProduct === 0 ? 0 : fkProduct,
      remise_percent: Number(line.remise_percent ?? 0),
      price_base_type: "HT",
      info_bits: 0,
      product_type: Number(line.product_type ?? 0),
      rang: line.rang != null ? Number(line.rang) : 0,
      special_code: line.special_code != null ? Number(line.special_code) : 0,
      fk_parent_line: 0,
      fk_fournprice: 0,
      pa_ht: paNum,
      label,
      date_start: "",
      date_end: "",
      fk_unit: 0,
      origin: "",
      origin_id: 0,
      multicurrency_subprice: 0,
      fk_remise_except: 0,
    };

    if (line.array_options) {
      sanitized.array_options = line.array_options;
    }

    // Use singular /line endpoint (plural /lines expects an array)
    const response = await this.api.post(`/proposals/${proposalId}/line`, sanitized);
    return response.data;
  }

  async updateProposalLine(
    proposalId: string,
    lineId: string,
    line: Partial<ProposalLine>
  ): Promise<ProposalLine> {
    const response = await this.api.put(`/proposals/${proposalId}/lines/${lineId}`, line);
    return response.data;
  }

  async deleteProposalLine(proposalId: string, lineId: string): Promise<void> {
    await this.api.delete(`/proposals/${proposalId}/lines/${lineId}`);
  }

  async validateProposal(
    id: string,
    opts?: { notrigger?: 0 | 1; timeoutMs?: number }
  ): Promise<Proposal> {
    const notrigger = opts?.notrigger ?? 0;
    const timeoutMs = opts?.timeoutMs ?? 120000;
    // validateStatus: évite l’intercepteur Axios qui masque le corps JSON d’erreur (ex. RestException 500).
    const response = await this.api.post<unknown>(
      `/proposals/${id}/validate`,
      { notrigger },
      { timeout: timeoutMs, validateStatus: () => true }
    );
    const st = response.status;
    if (st >= 200 && st < 300) {
      return response.data as Proposal;
    }
    // Dolibarr : « rien à faire » si déjà validé — on renvoie l’état courant plutôt qu’une erreur.
    if (st === 304) {
      return await this.getProposal(id);
    }
    const { message, raw } = parseDolibarrErrorBody(response.data);
    const base =
      message || `Échec de la validation du devis (HTTP ${st})`;
    if (typeof console !== "undefined" && console.error) {
      console.error("[DolibarrClient.validateProposal]", {
        proposalId: id,
        notrigger,
        httpStatus: st,
        body: raw,
      });
    }
    throw new DolibarrAPIError(base, st, "VALIDATE_ERROR", raw);
  }

  async closeProposal(id: string, status: 2 | 3): Promise<Proposal> {
    // 2=signed, 3=not signed
    const response = await this.api.post(`/proposals/${id}/close`, { status, notrigger: 0 });
    return response.data;
  }

  async deleteProposal(id: string): Promise<void> {
    await this.api.delete(`/proposals/${id}`);
  }

  async convertProposalToOrder(id: string): Promise<string> {
    // Try native Dolibarr endpoint first (v18+)
    try {
      const response = await this.api.post(`/proposals/${id}/createorder`);
      const orderId = typeof response.data === 'object' ? response.data.id || response.data : response.data;
      return String(orderId);
    } catch (e: any) {
      // If endpoint doesn't exist (404/405), fall back to manual creation
      if (e?.status !== 404 && e?.status !== 405 && e?.code !== 'NOT_FOUND') {
        throw e;
      }
    }

    // Fallback: manually create order and copy lines from proposal
    const proposal = await this.getProposal(id);

    const orderId = await this.createOrder({
      socid: proposal.socid,
      date: Math.floor(Date.now() / 1000),
      note_public: proposal.note_public,
      note_private: proposal.note_private,
      ref_client: proposal.ref_client,
      cond_reglement_code: proposal.cond_reglement_code,
      mode_reglement_code: proposal.mode_reglement_code,
      fk_project: proposal.fk_project,
    });

    const lineErrors: string[] = [];
    if (proposal.lines && proposal.lines.length > 0) {
      for (let i = 0; i < proposal.lines.length; i++) {
        const line = proposal.lines[i];
        try {
          await this.addOrderLine(orderId, {
            desc: line.description || line.label || "",
            subprice: Number(line.subprice) || 0,
            qty: Number(line.qty) || 1,
            tva_tx: Number(line.tva_tx) || 20,
            product_type: line.product_type || 0,
            rang: i + 1,
            ...(line.fk_product && line.fk_product !== "0" && { fk_product: line.fk_product }),
            ...(line.remise_percent && { remise_percent: Number(line.remise_percent) }),
          });
        } catch (err: any) {
          lineErrors.push(`Ligne ${i + 1}: ${err.message || 'erreur inconnue'}`);
        }
      }
    }

    if (lineErrors.length > 0) {
      console.warn("Erreurs lors de la copie des lignes:", lineErrors);
    }

    return orderId;
  }

  // Orders
  async getOrders(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    thirdparty_ids?: string;
    status?: string;
    sqlfilters?: string;
  }): Promise<Order[]> {
    try {
      const response = await this.api.get("/orders", { params });
      return normalizeDolibarrListResponse<Order>(response.data);
    } catch (e: any) {
      if (e?.status === 404) return [];
      throw e;
    }
  }

  async getOrdersPaginated(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    thirdparty_ids?: string;
    status?: string;
  }): Promise<PaginatedResponse<Order>> {
    const data = await this.getOrders(params);
    return this.createPaginatedResponse(data, params || {});
  }

  async getOrder(id: string): Promise<Order> {
    const response = await this.api.get(`/orders/${id}`);
    const order = response.data;

    if (order.socid && !order.thirdparty) {
      try {
        order.thirdparty = await this.getThirdParty(order.socid);
      } catch {
        // Silently fail - UI will show fallback
      }
    }

    return order;
  }

  async createOrder(data: {
    socid: string;
    date?: number;
    ref_client?: string;
    cond_reglement_code?: string;
    mode_reglement_code?: string;
    note_public?: string;
    note_private?: string;
    fk_project?: string;
  }): Promise<string> {
    const response = await this.api.post("/orders", data);
    return response.data;
  }

  async addOrderLine(
    orderId: string,
    line: {
      desc: string;
      subprice: number;
      qty: number;
      tva_tx?: number;
      fk_product?: string;
      remise_percent?: number;
      product_type?: 0 | 1 | 9;
      rang?: number;
    }
  ): Promise<string> {
    const response = await this.api.post(`/orders/${orderId}/lines`, line);
    return response.data;
  }

  async convertOrderToInvoice(id: string): Promise<string> {
    // Convert an order to an invoice
    const response = await this.api.post(`/orders/${id}/createinvoice`);
    return response.data; // Returns new invoice ID
  }

  // Invoices
  async getInvoices(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    thirdparty_ids?: string;
    status?: string;
    sqlfilters?: string;
  }): Promise<Invoice[]> {
    try {
      const response = await this.api.get("/invoices", { params });
      return normalizeDolibarrListResponse<Invoice>(response.data);
    } catch (e: any) {
      if (e?.status === 404) return [];
      throw e;
    }
  }

  async getInvoicesPaginated(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    thirdparty_ids?: string;
    status?: string;
  }): Promise<PaginatedResponse<Invoice>> {
    const data = await this.getInvoices(params);
    return this.createPaginatedResponse(data, params || {});
  }

  async getInvoice(id: string): Promise<Invoice> {
    const response = await this.api.get(`/invoices/${id}`);
    const invoice = response.data;

    if (invoice.socid && !invoice.thirdparty) {
      try {
        invoice.thirdparty = await this.getThirdParty(invoice.socid);
      } catch {
        // Silently fail - UI will show fallback
      }
    }

    return invoice;
  }

  async createInvoice(data: {
    socid: string;
    date?: number;
    ref_client?: string;
    cond_reglement_code?: string;
    mode_reglement_code?: string;
    note_public?: string;
    note_private?: string;
    fk_project?: string;
    array_options?: Record<string, unknown>;
  }): Promise<string> {
    const response = await this.api.post("/invoices", data);
    return response.data;
  }

  async addInvoiceLine(
    invoiceId: string,
    line: {
      desc: string;
      subprice: number;
      qty: number;
      tva_tx?: number;
      fk_product?: string;
      remise_percent?: number;
      product_type?: 0 | 1 | 9;
      rang?: number;
      special_code?: number;
      pa_ht?: number;
    }
  ): Promise<string> {
    const response = await this.api.post(`/invoices/${invoiceId}/lines`, line);
    return response.data;
  }

  // Projects
  async getProjects(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    thirdparty_ids?: string;
    status?: string;
    sqlfilters?: string;
  }): Promise<Project[]> {
    try {
      const response = await this.api.get("/projects", { params });
      const projects = normalizeDolibarrListResponse<Project>(response.data);
      // Map thirdparty_name to a lightweight thirdparty object for display
      return projects.map(p => {
        if (p.thirdparty_name && !p.thirdparty && p.socid) {
          p.thirdparty = {
            id: p.socid,
            name: p.thirdparty_name,
            client: "1",
            fournisseur: "0",
            status: "1",
          };
        }
        return p;
      });
    } catch (e: any) {
      if (e?.status === 404) return [];
      throw e;
    }
  }

  async getProjectsPaginated(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    thirdparty_ids?: string;
    status?: string;
  }): Promise<PaginatedResponse<Project>> {
    const data = await this.getProjects(params);
    return this.createPaginatedResponse(data, params || {});
  }

  async getProject(id: string): Promise<Project> {
    const response = await this.api.get(`/projects/${id}`);
    const project = response.data;

    if (project.socid && !project.thirdparty) {
      try {
        project.thirdparty = await this.getThirdParty(project.socid);
      } catch {
        // Silently fail - UI will show fallback
      }
    }

    return project;
  }

  async createProject(data: Partial<Project>): Promise<string> {
    const response = await this.api.post("/projects", data);
    return response.data;
  }

  async updateProject(id: string, data: Partial<Project>): Promise<Project> {
    const response = await this.api.put(`/projects/${id}`, data);
    return response.data;
  }

  async deleteProject(id: string): Promise<void> {
    await this.api.delete(`/projects/${id}`);
  }

  // Products
  async getProducts(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    mode?: number;
    variant_filter?: number;
    category?: number;
    sqlfilters?: string;
  }): Promise<Product[]> {
    try {
      const response = await this.api.get("/products", { params });
      return response.data;
    } catch (e: any) {
      if (e?.status === 404) return [];
      throw e;
    }
  }

  async getProduct(id: string): Promise<Product> {
    const response = await this.api.get(`/products/${id}`);
    return response.data;
  }

  async updateProduct(id: string, data: Partial<Product>): Promise<Product> {
    const response = await this.api.put(`/products/${id}`, data);
    return response.data;
  }

  async addProductSupplierPrice(
    productId: string,
    data: {
      fk_soc: string;
      ref_fourn: string;
      price: number;
      quantity?: number;
      tva_tx?: number;
    }
  ): Promise<string> {
    const response = await this.api.post(`/products/${productId}/purchase_prices`, data);
    return response.data;
  }

  async searchProducts(query: string): Promise<Product[]> {
    const q = sanitizeSqlFilter(query);
    try {
      const response = await this.api.get("/products", {
        params: {
          limit: 20,
          sqlfilters: `((t.ref:like:'%${q}%') or (t.label:like:'%${q}%'))`,
        },
      });
      return response.data;
    } catch (e: any) {
      if (e?.status === 404) return [];
      throw e;
    }
  }

  async getProductSupplierPrices(productId: string): Promise<SupplierPrice[]> {
    try {
      const response = await this.api.get(`/products/${productId}/purchase_prices`);
      return response.data;
    } catch (e: any) {
      if (e?.status === 404) return [];
      throw e;
    }
  }

  // Contacts
  async getContacts(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    thirdparty_ids?: string;
  }): Promise<Contact[]> {
    try {
      const response = await this.api.get("/contacts", { params });
      return response.data;
    } catch (e: any) {
      if (e?.status === 404) return [];
      throw e;
    }
  }

  async createContact(data: Partial<Contact>): Promise<string> {
    const response = await this.api.post("/contacts", data);
    return response.data;
  }

  // Documents / PDF

  /** Statuts pour lesquels on lit le corps JSON (évite l’intercepteur 404 sans perdre 401/403). */
  private static buildDocValidateStatus(status: number): boolean {
    return (
      status === 200 ||
      status === 201 ||
      status === 404 ||
      status === 400 ||
      status === 422 ||
      status === 500
    );
  }

  /**
   * Dolibarr peut renvoyer le PDF directement dans le JSON du PUT /documents/builddoc
   * (content + encoding base64). On l’utilise en priorité avant GET /documents/download.
   */
  private blobFromDolibarrJsonPayload(data: unknown): Blob | null {
    if (!data || typeof data !== "object") return null;
    const d = data as Record<string, unknown>;
    if (d.error) return null;
    const b64 = (d.content ?? d.filecontent) as string | undefined;
    if (!b64 || typeof b64 !== "string") return null;
    const enc = String(d.encoding || "").toLowerCase();
    if (enc && enc !== "base64") return null;
    try {
      const byteChars = atob(String(b64).replace(/\s/g, ""));
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteArray[i] = byteChars.charCodeAt(i);
      }
      const mimeType = String(
        (d["content-type"] as string) || (d.content_type as string) || "application/pdf"
      );
      const out = new Blob([byteArray], { type: mimeType.split(";")[0] });
      if (out.size === 0) return null;
      return out;
    } catch {
      return null;
    }
  }

  private async tryBuildDocumentBlob(
    modulepart: string,
    ref: string,
    template?: string
  ): Promise<Blob | null> {
    const response = await this.api.put<unknown>(
      "/documents/builddoc",
      {
        modulepart,
        original_file: `${ref}/${ref}.pdf`,
        langcode: "fr_FR",
        ...(template !== undefined && template !== "" ? { doctemplate: template } : {}),
      },
      {
        validateStatus: DolibarrClient.buildDocValidateStatus,
        timeout: 120000,
      }
    );
    if (response.status !== 200 && response.status !== 201) return null;
    return this.blobFromDolibarrJsonPayload(response.data);
  }

  private uniqueRefVariants(primary: string, apiRef?: string): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    const add = (r: string | undefined) => {
      if (!r) return;
      const t = r.trim();
      if (!t) return;
      if (!seen.has(t)) {
        seen.add(t);
        out.push(t);
      }
      const stripped = t.replace(/^\(|\)$/g, "");
      if (stripped && stripped !== t && !seen.has(stripped)) {
        seen.add(stripped);
        out.push(stripped);
      }
    };
    add(primary);
    add(apiRef);
    return out;
  }

  async buildDocument(modulepart: string, ref: string, template?: string): Promise<void> {
    await this.api.put(
      "/documents/builddoc",
      {
        modulepart,
        original_file: `${ref}/${ref}.pdf`,
        langcode: "fr_FR",
        ...(template && { doctemplate: template }),
      },
      {
        validateStatus: DolibarrClient.buildDocValidateStatus,
        timeout: 120000,
      }
    );
  }

  async getProposalPDF(ref: string, proposalId?: string): Promise<Blob> {
    if (!ref) {
      throw new DolibarrAPIError(
        "Référence du devis manquante.",
        400,
        "VALIDATION_ERROR"
      );
    }
    let apiRef: string | undefined;
    if (proposalId) {
      try {
        const p = await this.getProposal(proposalId);
        if (p?.ref) apiRef = String(p.ref).trim();
      } catch {
        /* ref UI uniquement */
      }
    }
    const refVariants = this.uniqueRefVariants(ref, apiRef);
    /** Dolibarr REST: seul `propal` est accepté par dol_check_secure_access_document pour les devis. */
    const moduleparts = ["propal"] as const;
    const templates = ["sudelec", "pdf_sudelec", undefined, "azur", "einstein", "crabe"] as const;

    for (const workRef of refVariants) {
      for (const mp of moduleparts) {
        for (const tpl of templates) {
          const blob = await this.tryBuildDocumentBlob(mp, workRef, tpl);
          if (blob && blob.size > 0) return blob;
        }
      }
    }

    for (const workRef of refVariants) {
      for (const mp of moduleparts) {
        try {
          return await this.downloadDocument(mp, `${workRef}/${workRef}.pdf`);
        } catch {
          /* essai suivant */
        }
      }
    }

    if (proposalId) {
      const found = await this.findPdfViaDocumentList(["propal"], proposalId);
      if (found) return await this.downloadDocument(found.modulepart, found.path);
    }

    throw new DolibarrAPIError(
      "PDF du devis introuvable. Vérifiez le modèle dans Dolibarr ou générez le document une fois.",
      404,
      "PDF_NOT_FOUND"
    );
  }

  async getInvoicePDF(ref: string, entityId?: string): Promise<Blob> {
    if (!ref) {
      throw new DolibarrAPIError("Référence facture manquante.", 400, "VALIDATION_ERROR");
    }
    let apiRef: string | undefined;
    if (entityId) {
      try {
        const inv = await this.getInvoice(entityId);
        if (inv?.ref) apiRef = String(inv.ref).trim();
      } catch {
        /* */
      }
    }
    const refVariants = this.uniqueRefVariants(ref, apiRef);
    const moduleparts = ["facture", "invoice"] as const;
    const templates = ["sudelec", undefined, "crabe", "einstein", "azur"] as const;

    for (const workRef of refVariants) {
      for (const mp of moduleparts) {
        for (const tpl of templates) {
          const blob = await this.tryBuildDocumentBlob(mp, workRef, tpl);
          if (blob && blob.size > 0) return blob;
        }
      }
    }

    for (const workRef of refVariants) {
      for (const mp of moduleparts) {
        try {
          return await this.downloadDocument(mp, `${workRef}/${workRef}.pdf`);
        } catch {
          /* */
        }
      }
    }

    if (entityId) {
      const found = await this.findPdfViaDocumentList(["facture", "invoice"], entityId);
      if (found) return await this.downloadDocument(found.modulepart, found.path);
    }

    throw new DolibarrAPIError(
      "PDF facture introuvable. Générez le PDF dans Dolibarr si nécessaire.",
      404,
      "PDF_NOT_FOUND"
    );
  }

  async getOrderPDF(ref: string, entityId?: string): Promise<Blob> {
    if (!ref) {
      throw new DolibarrAPIError("Référence commande manquante.", 400, "VALIDATION_ERROR");
    }
    let apiRef: string | undefined;
    if (entityId) {
      try {
        const ord = await this.getOrder(entityId);
        if (ord?.ref) apiRef = String(ord.ref).trim();
      } catch {
        /* */
      }
    }
    const refVariants = this.uniqueRefVariants(ref, apiRef);
    const moduleparts = ["commande", "order"] as const;
    const templates = ["einstein", undefined, "crabe", "azur"] as const;

    for (const workRef of refVariants) {
      for (const mp of moduleparts) {
        for (const tpl of templates) {
          const blob = await this.tryBuildDocumentBlob(mp, workRef, tpl);
          if (blob && blob.size > 0) return blob;
        }
      }
    }

    for (const workRef of refVariants) {
      for (const mp of moduleparts) {
        try {
          return await this.downloadDocument(mp, `${workRef}/${workRef}.pdf`);
        } catch {
          /* */
        }
      }
    }

    if (entityId) {
      const found = await this.findPdfViaDocumentList(["commande", "order"], entityId);
      if (found) return await this.downloadDocument(found.modulepart, found.path);
    }

    throw new DolibarrAPIError(
      "PDF commande introuvable. Générez le PDF dans Dolibarr si nécessaire.",
      404,
      "PDF_NOT_FOUND"
    );
  }

  async getContractPDF(ref: string): Promise<Blob> {
    return this.downloadDocument("contrat", `${ref}/${ref}.pdf`);
  }

  async getInterventionPDF(ref: string): Promise<Blob> {
    const refs = this.uniqueRefVariants(ref);
    for (const workRef of refs) {
      const blob = await this.tryBuildDocumentBlob("ficheinter", workRef, "soleil");
      if (blob && blob.size > 0) return blob;
    }
    for (const workRef of refs) {
      try {
        return await this.downloadDocument("ficheinter", `${workRef}/${workRef}.pdf`);
      } catch {
        /* essai suivant */
      }
    }
    throw new DolibarrAPIError(
      "PDF intervention introuvable. Générez le document dans Dolibarr si nécessaire.",
      404,
      "PDF_NOT_FOUND"
    );
  }

  async generateSupplierOrderPDF(id: string): Promise<void> {
    await this.api.post(`/supplierorders/${id}/generatepdf`, {});
  }

  async getSupplierOrderPDF(ref: string, id?: string): Promise<Blob> {
    try {
      return await this.downloadDocument("fournisseur", `commande/${ref}/${ref}.pdf`);
    } catch {
      // PDF not generated yet — generate then retry
      if (id) {
        await this.generateSupplierOrderPDF(id);
        return this.downloadDocument("fournisseur", `commande/${ref}/${ref}.pdf`);
      }
      throw new DolibarrAPIError("PDF introuvable", 404, "PDF_NOT_FOUND");
    }
  }

  // Interventions
  async getInterventions(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    thirdparty_ids?: string;
    sqlfilters?: string;
  }): Promise<Intervention[]> {
    try {
      const response = await this.api.get("/interventions", { params });
      return response.data;
    } catch (e: any) {
      if (e?.status === 404) return [];
      throw e;
    }
  }

  async getInterventionsPaginated(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    thirdparty_ids?: string;
    sqlfilters?: string;
  }): Promise<PaginatedResponse<Intervention>> {
    const data = await this.getInterventions(params);
    return this.createPaginatedResponse(data, params || {});
  }

  async getIntervention(id: string): Promise<Intervention> {
    const response = await this.api.get(`/interventions/${id}`);
    const intervention = response.data;

    if (intervention.socid && !intervention.thirdparty) {
      try {
        intervention.thirdparty = await this.getThirdParty(intervention.socid);
      } catch {
        // Silently fail - UI will show fallback
      }
    }

    return intervention;
  }

  async createIntervention(data: {
    socid: string;
    fk_project?: string;
    description?: string;
    datei?: number; // Date intervention (timestamp)
    note_public?: string;
    note_private?: string;
  }): Promise<string> {
    const response = await this.api.post("/interventions", data);
    return response.data;
  }

  async updateIntervention(
    id: string,
    data: Partial<Intervention>
  ): Promise<Intervention> {
    const response = await this.api.put(`/interventions/${id}`, data);
    return response.data;
  }

  async deleteIntervention(id: string): Promise<void> {
    await this.api.delete(`/interventions/${id}`);
  }

  async validateIntervention(id: string): Promise<Intervention> {
    const response = await this.api.post(`/interventions/${id}/validate`);
    return response.data;
  }

  async closeIntervention(id: string): Promise<Intervention> {
    const response = await this.api.post(`/interventions/${id}/close`);
    return response.data;
  }

  async addInterventionLine(
    interventionId: string,
    line: {
      desc: string;
      date: number; // timestamp
      duration: number; // durée en secondes
    }
  ): Promise<string> {
    const response = await this.api.post(`/interventions/${interventionId}/lines`, line);
    return response.data;
  }

  async deleteInterventionLine(interventionId: string, lineId: string): Promise<void> {
    await this.api.delete(`/interventions/${interventionId}/lines/${lineId}`);
  }

  // Contracts
  async getContracts(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    thirdparty_ids?: string;
    sqlfilters?: string;
  }): Promise<Contract[]> {
    try {
      const response = await this.api.get("/contracts", { params });
      return response.data;
    } catch (e: any) {
      if (e?.status === 404) return [];
      throw e;
    }
  }

  async getContractsPaginated(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    thirdparty_ids?: string;
    sqlfilters?: string;
  }): Promise<PaginatedResponse<Contract>> {
    const data = await this.getContracts(params);
    return this.createPaginatedResponse(data, params || {});
  }

  async getContract(id: string): Promise<Contract> {
    const response = await this.api.get(`/contracts/${id}`);
    const contract = response.data;

    if (contract.socid && !contract.thirdparty) {
      try {
        contract.thirdparty = await this.getThirdParty(contract.socid);
      } catch {
        // Silently fail - UI will show fallback
      }
    }

    return contract;
  }

  async createContract(data: {
    socid: string;
    date_contrat?: number;
    ref_customer?: string;
    fk_project?: string;
    note_public?: string;
    note_private?: string;
  }): Promise<string> {
    const response = await this.api.post("/contracts", data);
    return response.data;
  }

  async updateContract(id: string, data: Partial<Contract>): Promise<Contract> {
    const response = await this.api.put(`/contracts/${id}`, data);
    return response.data;
  }

  async deleteContract(id: string): Promise<void> {
    await this.api.delete(`/contracts/${id}`);
  }

  async validateContract(id: string): Promise<Contract> {
    const response = await this.api.post(`/contracts/${id}/validate`);
    return response.data;
  }

  async closeContract(id: string): Promise<Contract> {
    const response = await this.api.post(`/contracts/${id}/close`);
    return response.data;
  }

  async addContractLine(
    contractId: string,
    line: {
      desc: string;
      subprice: number;
      qty: number;
      tva_tx?: number;
      fk_product?: string;
      product_type?: 0 | 1;
      date_start?: number; // timestamp
      date_end?: number; // timestamp
    }
  ): Promise<string> {
    const response = await this.api.post(`/contracts/${contractId}/lines`, line);
    return response.data;
  }

  async updateContractLine(
    contractId: string,
    lineId: string,
    line: Partial<ContractLine>
  ): Promise<ContractLine> {
    const response = await this.api.put(`/contracts/${contractId}/lines/${lineId}`, line);
    return response.data;
  }

  async deleteContractLine(contractId: string, lineId: string): Promise<void> {
    await this.api.delete(`/contracts/${contractId}/lines/${lineId}`);
  }

  async activateContractLine(contractId: string, lineId: string, date_start?: number): Promise<void> {
    const response = await this.api.post(`/contracts/${contractId}/lines/${lineId}/activate`, {
      date_start: date_start || Math.floor(Date.now() / 1000),
    });
    return response.data;
  }

  async closeContractLine(contractId: string, lineId: string, date_end?: number): Promise<void> {
    const response = await this.api.post(`/contracts/${contractId}/lines/${lineId}/close`, {
      date_end: date_end || Math.floor(Date.now() / 1000),
    });
    return response.data;
  }

  // Supplier Orders
  async getSupplierOrders(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    thirdparty_ids?: string;
    sqlfilters?: string;
  }): Promise<SupplierOrder[]> {
    try {
      const response = await this.api.get("/supplierorders", { params });
      return response.data;
    } catch (e: any) {
      if (e?.status === 404) return [];
      throw e;
    }
  }

  async getSupplierOrdersPaginated(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    thirdparty_ids?: string;
    sqlfilters?: string;
  }): Promise<PaginatedResponse<SupplierOrder>> {
    const data = await this.getSupplierOrders(params);
    return this.createPaginatedResponse(data, params || {});
  }

  async getSupplierOrder(id: string): Promise<SupplierOrder> {
    const response = await this.api.get(`/supplierorders/${id}`);
    const order = response.data;

    if (order.socid && !order.thirdparty) {
      try {
        order.thirdparty = await this.getThirdParty(order.socid);
      } catch {
        // Silently fail - UI will show fallback
      }
    }

    return order;
  }

  async createSupplierOrder(data: {
    socid: string; // ID du fournisseur
    date?: number;
    ref_supplier?: string;
    fk_project?: string;
    note_public?: string;
    note_private?: string;
  }): Promise<string> {
    const response = await this.api.post("/supplierorders", data);
    return response.data;
  }

  async updateSupplierOrder(id: string, data: Partial<SupplierOrder>): Promise<SupplierOrder> {
    const response = await this.api.put(`/supplierorders/${id}`, data);
    return response.data;
  }

  async validateSupplierOrder(id: string): Promise<SupplierOrder> {
    const response = await this.api.post(`/supplierorders/${id}/validate`);
    return response.data;
  }

  async approveSupplierOrder(id: string): Promise<SupplierOrder> {
    const response = await this.api.post(`/supplierorders/${id}/approve`);
    return response.data;
  }

  async orderSupplierOrder(id: string): Promise<SupplierOrder> {
    // Mark as ordered (sent to supplier)
    const response = await this.api.post(`/supplierorders/${id}/order`);
    return response.data;
  }

  async receiveSupplierOrder(id: string, warehouse_id?: string): Promise<SupplierOrder> {
    // Mark as received (all products received)
    const response = await this.api.post(`/supplierorders/${id}/receive`, {
      warehouse_id,
    });
    return response.data;
  }

  async cancelSupplierOrder(id: string): Promise<SupplierOrder> {
    const response = await this.api.post(`/supplierorders/${id}/cancel`);
    return response.data;
  }

  async addSupplierOrderLine(
    orderId: string,
    line: {
      desc: string;
      subprice: number;
      qty: number;
      tva_tx?: number;
      fk_product?: string;
      ref_fourn?: string;
      product_type?: 0 | 1;
    }
  ): Promise<string> {
    // Dolibarr 18 doesn't expose POST /supplierorders/{id}/lines
    // Use custom endpoint via DoliUI proxy
    const isClient = typeof window !== 'undefined';
    if (isClient) {
      const res = await fetch('/api/suppliers/order-lines', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', DOLAPIKEY: this.apiKey },
        body: JSON.stringify({ order_id: orderId, action: 'addline', ...line }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Erreur serveur' }));
        throw new DolibarrAPIError(err.error || `HTTP ${res.status}`, res.status, 'API_ERROR');
      }
      const data = await res.json();
      return data.line_id || data;
    }
    // Server-side fallback: call custom endpoint directly
    const doliUrl = process.env.NEXT_PUBLIC_DOLIBARR_URL || '';
    const res = await fetch(
      `${doliUrl}/api/custom_supplier_lines.php?action=addline&order_id=${orderId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', DOLAPIKEY: this.apiKey },
        body: JSON.stringify(line),
      }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Erreur serveur' }));
      throw new DolibarrAPIError(err.error || `HTTP ${res.status}`, res.status, 'API_ERROR');
    }
    const data = await res.json();
    return data.line_id || data;
  }

  async updateSupplierOrderLine(
    orderId: string,
    lineId: string,
    line: Partial<SupplierOrderLine>
  ): Promise<SupplierOrderLine> {
    // Use custom endpoint for Dolibarr 18 compatibility
    const isClient = typeof window !== 'undefined';
    if (isClient) {
      const res = await fetch('/api/suppliers/order-lines', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', DOLAPIKEY: this.apiKey },
        body: JSON.stringify({ order_id: orderId, action: 'updateline', line_id: lineId, ...line }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Erreur serveur' }));
        throw new DolibarrAPIError(err.error || `HTTP ${res.status}`, res.status, 'API_ERROR');
      }
      return res.json();
    }
    const response = await this.api.put(`/supplierorders/${orderId}/lines/${lineId}`, line);
    return response.data;
  }

  async deleteSupplierOrderLine(orderId: string, lineId: string): Promise<void> {
    // Use custom endpoint for Dolibarr 18 compatibility
    const isClient = typeof window !== 'undefined';
    if (isClient) {
      const res = await fetch(`/api/suppliers/order-lines?order_id=${orderId}&line_id=${lineId}`, {
        method: 'DELETE',
        headers: { DOLAPIKEY: this.apiKey },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Erreur serveur' }));
        throw new DolibarrAPIError(err.error || `HTTP ${res.status}`, res.status, 'API_ERROR');
      }
      return;
    }
    await this.api.delete(`/supplierorders/${orderId}/lines/${lineId}`);
  }

  // Documents
  async getDocuments(params: {
    modulepart: string; // Type de module (propal, facture, order, contract, etc.)
    id: string; // ID de l'entité
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
  }): Promise<Document[]> {
    try {
      const response = await this.api.get("/documents", { params });
      return Array.isArray(response.data) ? response.data : [];
    } catch (e: unknown) {
      // Dolibarr returns 404 when no documents exist — this is not an error
      if (e instanceof DolibarrAPIError && e.status === 404) return [];
      if (e && typeof e === "object" && "status" in e && (e as { status: number }).status === 404) return [];
      throw e;
    }
  }

  /** Chemins PDF réels via GET /documents (liste), si ref/ref.pdf ne suffit pas. */
  private async findPdfViaDocumentList(
    moduleparts: string[],
    entityId: string
  ): Promise<{ modulepart: string; path: string } | null> {
    for (const mp of moduleparts) {
      try {
        const docs = await this.getDocuments({ modulepart: mp, id: entityId });
        const pdf = docs.find(
          (d) =>
            (d.type && String(d.type).toLowerCase().includes("pdf")) ||
            String(d.name || "")
              .toLowerCase()
              .endsWith(".pdf") ||
            String(d.path || "")
              .toLowerCase()
              .endsWith(".pdf")
        );
        if (pdf?.path) return { modulepart: mp, path: pdf.path };
      } catch {
        /* modulepart suivant */
      }
    }
    return null;
  }

  /**
   * Dolibarr renvoie en général du JSON { content, encoding: "base64" }.
   * Avec responseType "blob" (recommandé derrière le proxy), il faut parser le texte ou détecter un PDF binaire.
   */
  private async parseDownloadBlobResponse(response: {
    status: number;
    data: Blob;
  }): Promise<Blob> {
    const { status, data: blob } = response;
    if (!(blob instanceof Blob)) {
      throw new DolibarrAPIError("Réponse téléchargement invalide.", 0, "INVALID_DOCUMENT");
    }

    if (status !== 200) {
      let detail = `HTTP ${status}`;
      try {
        const t = await blob.text();
        const j = JSON.parse(t) as {
          error?: { message?: string } | string;
          message?: string;
        };
        if (typeof j?.error === "object" && j.error?.message) detail = String(j.error.message);
        else if (typeof j?.error === "string") detail = j.error;
        else if (typeof j?.message === "string") detail = j.message;
      } catch {
        /* garder detail */
      }
      throw new DolibarrAPIError(
        detail || "Ressource introuvable.",
        status,
        status === 404 ? "NOT_FOUND" : "DOWNLOAD_ERROR"
      );
    }

    const headBuf = await blob.slice(0, 4).arrayBuffer();
    const head = new TextDecoder("latin1").decode(headBuf);
    if (head.startsWith("%PDF")) {
      if (blob.size === 0) {
        throw new DolibarrAPIError("Le document PDF est vide.", 0, "EMPTY_DOCUMENT");
      }
      return blob;
    }

    const text = await blob.text();
    let data: Record<string, unknown>;
    try {
      data = JSON.parse(text) as Record<string, unknown>;
    } catch {
      throw new DolibarrAPIError(
        "Le serveur n'a pas renvoyé un PDF exploitable.",
        0,
        "INVALID_DOCUMENT"
      );
    }

    if (data.error) {
      const err = data.error as { message?: string } | string;
      const msg =
        typeof err === "object" && err?.message
          ? String(err.message)
          : typeof err === "string"
            ? err
            : "Erreur Dolibarr";
      throw new DolibarrAPIError(msg, status, "API_ERROR");
    }

    const b64 = (data.content ?? data.filecontent) as string | undefined;
    const enc = String(data.encoding || "").toLowerCase();
    if (b64 && (!enc || enc === "base64")) {
      try {
        const byteChars = atob(String(b64).replace(/\s/g, ""));
        const byteArray = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteArray[i] = byteChars.charCodeAt(i);
        }
        const mimeType = String(
          (data["content-type"] as string) || (data.content_type as string) || "application/pdf"
        );
        const out = new Blob([byteArray], { type: mimeType.split(";")[0] });
        if (out.size === 0) {
          throw new DolibarrAPIError("Le document PDF est vide.", 0, "EMPTY_DOCUMENT");
        }
        return out;
      } catch (e) {
        if (e instanceof DolibarrAPIError) throw e;
        throw new DolibarrAPIError("Décodage base64 impossible.", 0, "INVALID_DOCUMENT");
      }
    }

    throw new DolibarrAPIError(
      "Format de document inattendu (pas de contenu base64).",
      0,
      "INVALID_DOCUMENT"
    );
  }

  async downloadDocument(modulepart: string, original_file: string): Promise<Blob> {
    const response = await this.api.get<Blob>(`/documents/download`, {
      params: { modulepart, original_file },
      timeout: 120000,
      responseType: "blob",
      validateStatus: () => true,
    });
    return this.parseDownloadBlobResponse(response);
  }

  async deleteDocument(modulepart: string, original_file: string): Promise<void> {
    await this.api.delete(`/documents`, {
      params: { modulepart, original_file },
    });
  }

  // Agenda Events
  async getAgendaEvents(params?: {
    sortfield?: string;
    sortorder?: "ASC" | "DESC";
    limit?: number;
    page?: number;
    sqlfilters?: string;
  }): Promise<Record<string, unknown>[]> {
    try {
      const response = await this.api.get("/agendaevents", { params });
      return response.data;
    } catch (e: any) {
      if (e?.status === 404) return [];
      throw e;
    }
  }

  async createAgendaEvent(data: {
    label: string;
    type_code?: string;
    datep: number;
    datef?: number;
    userownerid?: number;
    note?: string;
    fk_element?: number;
    elementtype?: string;
    percentage?: number;
    socid?: number;
  }): Promise<string> {
    const response = await this.api.post("/agendaevents", {
      ...data,
      type_code: data.type_code || "AC_OTH",
    });
    return response.data;
  }

  async updateAgendaEvent(id: string, data: Record<string, unknown>): Promise<void> {
    await this.api.put(`/agendaevents/${id}`, data);
  }

  // Stats helpers
  async getDashboardStats(): Promise<{
    proposals: { draft: number; validated: number; signed: number; total_ht: number };
    invoices: { unpaid: number; paid: number; total_unpaid_ht: number };
    orders: { open: number; total_ht: number };
    thirdparties: { clients: number; prospects: number };
  }> {
    // Use allSettled so a single module permission error doesn't crash the dashboard
    const [proposalsResult, invoicesResult, ordersResult, thirdpartiesResult] = await Promise.allSettled([
      this.getProposals({ limit: 100 }),
      this.getInvoices({ limit: 100 }),
      this.getOrders({ limit: 100 }),
      this.getThirdParties({ limit: 100 }),
    ]);

    const proposals = proposalsResult.status === "fulfilled" ? proposalsResult.value : [];
    const invoices = invoicesResult.status === "fulfilled" ? invoicesResult.value : [];
    const orders = ordersResult.status === "fulfilled" ? ordersResult.value : [];
    const thirdparties = thirdpartiesResult.status === "fulfilled" ? thirdpartiesResult.value : [];

    return {
      proposals: {
        draft: proposals.filter((p) => p.status === 0).length,
        validated: proposals.filter((p) => p.status === 1).length,
        signed: proposals.filter((p) => p.status === 2).length,
        total_ht: proposals.reduce((acc, p) => acc + (p.total_ht || 0), 0),
      },
      invoices: {
        unpaid: invoices.filter((i) => i.paye === "0" && i.status === 1).length,
        paid: invoices.filter((i) => i.paye === "1").length,
        total_unpaid_ht: invoices
          .filter((i) => i.paye === "0" && i.status === 1)
          .reduce((acc, i) => acc + (i.remaintopay || i.total_ht || 0), 0),
      },
      orders: {
        open: orders.filter((o) => o.status === 1 || o.status === 2).length,
        total_ht: orders.reduce((acc, o) => acc + (o.total_ht || 0), 0),
      },
      thirdparties: {
        clients: thirdparties.filter((t) => t.client === "1" || t.client === "3").length,
        prospects: thirdparties.filter((t) => t.client === "2" || t.client === "3").length,
      },
    };
  }
}

// Helper to get or create client instance
export function getDolibarrClient(apiKey?: string): DolibarrClient | null {
  if (typeof window === "undefined") return null;

  const key = apiKey || localStorage.getItem("dolapikey");
  if (!key) return null;

  if (!clientInstance || (apiKey && apiKey !== localStorage.getItem("dolapikey"))) {
    clientInstance = new DolibarrClient(key);
  }

  return clientInstance;
}

// Server-side client factory
export function createServerDolibarrClient(apiKey: string): DolibarrClient {
  return new DolibarrClient(apiKey);
}
